import Column from '@amzn/meridian/column';
import { FieldArray, FormikProps, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Text from '@amzn/meridian/text';

import {
  CoachingFormData,
  ContactInput,
  ContactStatusOptionsObject,
  defaultContactReviewValues,
  RecommendedContactStatusOptionsObject,
} from '../../../../api/CoachingForm/schema';
import DataState from '../../../../Types/DataState';
import { ConsolidatedContact } from '../../../../api/CoachingNotes/schema';
import CROSubform from './CROSubform';

interface ContactReviewContainerProps {
  contactList: ConsolidatedContact[];
  contactListState: string;
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Container: React.FC<ContactReviewContainerProps> = ({
  contactList,
  contactListState,
  setShowToast,
}: ContactReviewContainerProps): React.ReactElement => {
  const { values }: FormikProps<CoachingFormData> = useFormikContext();
  const [initialValues] = useState<ContactInput[]>(
    values.contactReview || [defaultContactReviewValues]
  );

  let filteredContactList = contactList.filter(
    contact =>
      (contact.status === RecommendedContactStatusOptionsObject.REVIEWED ||
        contact.status ===
          RecommendedContactStatusOptionsObject.PENDINGCOACHING) &&
      (contact.contactStatus === ContactStatusOptionsObject.REVIEWED ||
        contact.contactStatus === ContactStatusOptionsObject.DELIVERED ||
        contact.contactStatus === null)
  );

  const maxReviewCount = 5;

  const contactsInUse: string[] = values.coachingDetails
    .map(d => d.relatedContacts)
    .flat();

  const contactInUse: boolean = initialValues.every(contact =>
    contactsInUse.includes(contact.cscContactID)
  );

  const intl = useIntl();

  const messages = {
    maxReviewLimitReached: intl.formatMessage(
      {
        id: 'cs_perfportal_contact_review_max_limit_reached',
        defaultMessage:
          'You have reached the maximum of {0} contact reviews per Coaching delivered.',
        description: 'Reached max limit for adding new reviews',
      },
      { 0: maxReviewCount }
    ),
    contactInUse: intl.formatMessage({
      id: 'cs_perfportal_contact_review_delete_disabled_inuse',
      defaultMessage: 'The contact ID is being used for a coaching topic.',
      description: 'Delete review disabled tooltip',
    }),
  };

  return (
    <FieldArray name='contactReview'>
      {(): React.ReactNode => {
        const handleReplace = (value: ContactInput[]): void => {
          values.contactReview = value;
        };

        return (
          <Column spacing='400'>
            {contactListState === DataState.LOADED &&
              (filteredContactList.length === 0 ? (
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_contact_recom_noContact'
                    defaultMessage='There are no contacts to display here at the moment.'
                  />
                </Text>
              ) : (
                <CROSubform
                  onsubmit={handleReplace}
                  initialValues={initialValues}
                  contactList={
                    contactListState === DataState.LOADED
                      ? filteredContactList
                      : []
                  }
                  coachingNote={values}
                  disabledTooltips={{
                    cscContactID: contactInUse ? messages.contactInUse : '',
                  }}
                />
              ))}
          </Column>
        );
      }}
    </FieldArray>
  );
};

export default Container;
